import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Timestamp } from '@bufbuild/protobuf';
import { Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraBadge, SpecraButton, SpecraCombobox, SpecraEntityCell, SpecraInput } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../../contexts/featureFlagContext';
import { useDebounce } from '../../../hooks/useDebounce';
import { useTenantAreas } from '../../../lib/access/useTenantAreas';
import { formatContractDate } from '../../../lib/contractDate';
import { RegistryListNote } from '../../Registries/list/RegistryListNote';
import { OpenFullPage, useRowOpen } from '../../Registries/list/RowOpen';
import { useContactSummaries } from '../../Registries/list/useRegistrySummaries';
import { useShowMore } from '../../Registries/list/useShowMore';
import { PEOPLE_MOVEMENT_FLAG, useRegistryRecordsEnabled } from '../../Registries/registryFlags';
import { registryHref } from '../../Registries/registryHref';
import { ConfirmDeleteDialog } from '../_shared/components/ConfirmDeleteDialog';
import { EntityFormDialog } from '../_shared/components/EntityFormDialog';
import { EntityTable } from '../_shared/components/EntityTable';
import { GovernmentOrgPicker } from '../_shared/components/GovernmentOrgPicker';
import { PlatformUserPicker } from '../_shared/components/PlatformUserPicker';
import { ScoreDetailDrawer } from '../_shared/components/ScoreDetailDrawer';
import { SectionNote } from '../_shared/components/SectionNote';
import { CONTACT_TYPE_OPTIONS, contactTypeLabel } from '../_shared/constants/contactTypes';
import { useTenantId } from '../_shared/hooks/useTenantId';
import { businessUnitHooks } from '../Org/hooks';
import { useTenantEnumValues } from '../Programs/referenceDataHooks';
import { ContactDetail } from './ContactDetail';
import { useContactsWithInactive, useReactivateContact, useRetireContact } from './contactLifecycle';
import { companyProfileHooks, contactHooks, useContactEmployments, useContactMergeEvents, useCreateContactEmployment, useEndContactEmployment, useLinkContactUser, useMergeContacts, useUnlinkContactUser, useUnmergeContacts } from './hooks';
import { RecordUnavailable } from './RecordUnavailable';
import { useRegistryQuery, useRegistryRecord } from './registryParams';
// Radix Select forbids an empty item value; one shared "none picked" sentinel
// serves both the company and role selects.
const NO_SELECTION = '__none__';
// ListContacts returns the whole tenant registry with no limit/offset, and
// EntityTable has no virtualization — mounting all of it is a multi-second
// main-thread stall once a tenant has tens of thousands of contacts (the RPC
// itself stays sub-second at that volume; the render is what stalls, SAD-323).
// 200 matches the existing `limit: 200` convention in hooks.ts. This caps what
// gets MOUNTED, not what gets fetched — search (server-side, below) is still
// the way to reach a row past the cap.
const REGISTRY_ROW_CAP = 200;
// Whether this person holds a platform account. Stored as nothing but the
// presence of contact.platformUserId — "contact only" IS the empty link, so
// there is no second flag to contradict it. The form keeps a third, UNANSWERED
// state so an internal contact can't be saved without someone deciding:
// landing on "no platform access" by leaving a field alone is exactly the
// silent outcome this choice exists to prevent.
const PLATFORM_UNANSWERED = '';
const PLATFORM_HAS_ACCOUNT = 'ACCOUNT';
const PLATFORM_CONTACT_ONLY = 'CONTACT_ONLY';
// Identity fields — shared by the create modal (as declarative fields) and the
// detail drawer's inline edit (as per-row `editField`s). The type select drives
// which affiliation input applies and renders above these; the affiliation
// group (company / gov org / role / platform access) renders as pickers.
const CONTACT_FIELDS = [
    {
        kind: 'text',
        name: 'firstName',
        label: 'First name',
        required: true,
        maxLength: 100
    },
    {
        kind: 'text',
        name: 'lastName',
        label: 'Last name',
        required: true,
        maxLength: 100
    },
    {
        kind: 'text',
        name: 'title',
        label: 'Title',
        maxLength: 200
    },
    {
        kind: 'text',
        name: 'corporateEmail',
        label: 'Company or corporate email',
        maxLength: 200,
        testId: Testids.deliveryRegistries.contactCorporateEmailInput
    },
    {
        kind: 'text',
        name: 'governmentEmail',
        label: 'Government or agency email',
        maxLength: 200,
        testId: Testids.deliveryRegistries.contactGovernmentEmailInput
    },
    {
        kind: 'text',
        name: 'phone',
        label: 'Phone',
        maxLength: 50
    },
    {
        kind: 'text',
        name: 'location',
        label: 'Location',
        maxLength: 200
    }
];
// The identity field a given kind maps to in the inline drawer. Keyed by name
// so the drawer rows reuse the exact FormFieldConfig the create form declares.
const fieldByName = (name)=>CONTACT_FIELDS.find((f)=>f.name === name);
export function ContactsTab() {
    const tenantId = useTenantId();
    const fullPage = useRegistryRecordsEnabled();
    const peopleMovementOn = useFeatureFlag(PEOPLE_MOVEMENT_FLAG);
    const areas = useTenantAreas();
    // The contact record itself (create, edit, link/unlink an account): admins
    // and internal pass like the server's requireContactRegistryAuthority;
    // everyone else needs area:registries. Employment history and merges are
    // shared-catalog writes the server gates on area:registries alone.
    const canEdit = areas.registries || areas.isAdmin || areas.isInternal;
    const canCurate = areas.registries;
    const [search, setSearch] = useRegistryQuery();
    // ryugu's ListContacts pulls the whole tenant registry and filters/enriches
    // it server-side, so un-debounced typing would fire a full fetch per
    // keystroke.
    const debouncedSearch = useDebounce(search, 300);
    const { data, isLoading } = contactHooks.useList({
        companyId: tenantId,
        search: debouncedSearch
    });
    // Deactivated contacts leave ordinary lists; the explicit inactive view asks
    // the server for them too (SAD-433).
    // REG-11: the Person page's Edit link asks for this view (?inactive=1) for a
    // deactivated contact, so the drawer can resolve the record.
    const [linkParams] = useSearchParams();
    const [showInactive, setShowInactive] = useState(()=>linkParams.get('inactive') === '1');
    const inactiveView = useContactsWithInactive({
        companyId: tenantId,
        search: debouncedSearch
    }, showInactive);
    const activeOnly = data?.items ?? [];
    const items = showInactive ? inactiveView.data?.items ?? activeOnly : activeOnly;
    // See REGISTRY_ROW_CAP above — only the rows handed to EntityTable are
    // capped; every other use of `items` (detail lookup, merge picker) keeps
    // the full fetched list.
    const paging = useShowMore(items, `${debouncedSearch}:${showInactive}`);
    const visibleItems = fullPage ? paging.visible : items.slice(0, REGISTRY_ROW_CAP);
    const summaries = useContactSummaries(fullPage ? visibleItems.map((row)=>row.id) : []);
    const summaryById = new Map((summaries.isError ? [] : summaries.data ?? []).map((row)=>[
            row.contactId,
            row
        ]));
    const companiesQuery = companyProfileHooks.useList({
        companyId: tenantId
    });
    const businessUnitsQuery = businessUnitHooks.useList({
        companyId: tenantId
    });
    const upsert = contactHooks.useUpsert();
    const remove = contactHooks.useDelete();
    const retire = useRetireContact();
    const reactivate = useReactivateContact();
    const link = useLinkContactUser();
    const unlink = useUnlinkContactUser();
    const [formOpen, setFormOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [deactivating, setDeactivating] = useState(null);
    // Detail drawer: store only the id and DERIVE the record from `items`, so an
    // open drawer always reflects the latest list without a re-sync effect that
    // infinite-loops on fresh-but-equal refs each render.
    // The id lives in the URL (?record=) — see registryParams.ts.
    const { recordId: detailId, openRecord, closeRecord } = useRegistryRecord();
    const detail = detailId ? items.find((c)=>c.id === detailId) ?? null : null;
    const recordUnavailable = !!detailId && !isLoading && !detail;
    // Whether the drawer opens in read or straight into edit (a row's Edit action).
    const [detailMode, setDetailMode] = useState('read');
    // REG-08: with registries_records_v1 on, a row opens the full Person page
    // (/registries/contacts/<id>) instead of the drawer. The row's Edit action
    // and ?record= deep links (the Person page's own Edit) still use the drawer.
    // Flag off: unchanged.
    const rowOpen = useRowOpen('CONTACT', ()=>setDetailMode('read'));
    const personHref = (id)=>fullPage ? registryHref({
            kind: 'CONTACT',
            id
        }) : null;
    // C1.1-05: a People row on the Company drawer used to link here with
    // ?contactId=; it now sends ?record=, and this keeps older links working.
    const [searchParams, setSearchParams] = useSearchParams();
    const legacyContactId = searchParams.get('contactId');
    useEffect(()=>{
        if (!legacyContactId) return;
        const next = new URLSearchParams(searchParams);
        next.delete('contactId');
        if (!next.has('record')) next.set('record', legacyContactId);
        setSearchParams(next, {
            replace: true
        });
    }, [
        legacyContactId,
        searchParams,
        setSearchParams
    ]);
    // Affiliation / role / platform live outside the RHF form (they're pickers and
    // selects driven by the type), so they're parent state — shared verbatim by the
    // create modal and the drawer's inline edit. Both surfaces seed them via
    // `seedFrom` (create → null defaults; drawer → the viewed record).
    const [contactType, setContactType] = useState('INTERNAL');
    const [governmentOrganizationId, setGovernmentOrganizationId] = useState('');
    const [companyProfileId, setCompanyProfileId] = useState(NO_SELECTION);
    const [businessUnitId, setBusinessUnitId] = useState('');
    const [roleTypeId, setRoleTypeId] = useState('');
    const [platformAccess, setPlatformAccess] = useState(PLATFORM_UNANSWERED);
    const [platformUserId, setPlatformUserId] = useState('');
    // The picked business unit scopes the role picker below it (SAD-75): a
    // BU-scoped tenant offers that BU's own role names plus the company-wide
    // ones, same NULL-or-match semantics ProgramRolesTab's picker already
    // relies on. No BU picked means no narrowing at all — every role name.
    const roleTypes = useTenantEnumValues('role_type', businessUnitId || undefined);
    // Labels (table + drawer) must resolve regardless of the picker's BU scope —
    // a contact's role can belong to a different business unit than whatever is
    // currently selected in the (still-open) create/edit form, so `roleTypes`
    // above is unsuitable for lookups: it would show '—' for that contact.
    const allRoleTypes = useTenantEnumValues('role_type');
    // People Graph (Phase 3 slice 2): the drawer's Employment history + the
    // minimal merge affordance. Employer for a new row is EXACTLY ONE of a
    // company or a gov org (the XOR the schema enforces).
    const employmentsQuery = useContactEmployments(detailId, tenantId);
    const employments = employmentsQuery.data ?? [];
    const createEmployment = useCreateContactEmployment();
    const endEmployment = useEndContactEmployment();
    const mergeContacts = useMergeContacts();
    const unmergeContacts = useUnmergeContacts();
    const mergeEventsQuery = useContactMergeEvents(detailId, tenantId);
    const mergeEvents = mergeEventsQuery.data ?? [];
    const openEmployments = employments.filter((e)=>!e.endDate).length;
    const [empKind, setEmpKind] = useState('COMPANY');
    const [empCompanyId, setEmpCompanyId] = useState('');
    const [empGovOrgId, setEmpGovOrgId] = useState('');
    const [empTitle, setEmpTitle] = useState('');
    const [empType, setEmpType] = useState('EMPLOYEE');
    const [empStart, setEmpStart] = useState('');
    const [empEnd, setEmpEnd] = useState('');
    const [mergeTargetId, setMergeTargetId] = useState('');
    // Reset the graph sub-forms whenever the drawer moves to a different
    // contact — the #997 stale-target lesson, applied at birth.
    useEffect(()=>{
        setEmpKind('COMPANY');
        setEmpCompanyId('');
        setEmpGovOrgId('');
        setEmpTitle('');
        setEmpType('EMPLOYEE');
        setEmpStart('');
        setEmpEnd('');
        setMergeTargetId('');
    }, [
        detailId
    ]);
    const ymdToTimestamp = (ymd)=>ymd ? Timestamp.fromDate(new Date(`${ymd}T00:00:00Z`)) : undefined;
    const companyOptions = useMemo(()=>(companiesQuery.data?.items ?? []).map((c)=>({
                value: c.id,
                label: c.name
            })), [
        companiesQuery.data?.items
    ]);
    const businessUnitOptions = useMemo(()=>(businessUnitsQuery.data?.items ?? []).map((b)=>({
                value: b.id,
                label: b.name
            })), [
        businessUnitsQuery.data?.items
    ]);
    const businessUnitLabel = (id)=>id ? (businessUnitsQuery.data?.items ?? []).find((b)=>b.id === id)?.name ?? '(unknown)' : 'Unscoped — visible in every business unit';
    // One entry per role name. The server's offering key is (code,
    // business_unit_id), so the same name can arrive more than once: the shared
    // `_system` row, the tenant's own company-wide override, and — now that the
    // picker asks for the selected business unit (SAD-75) — that BU's own local
    // override too. Collapse by code and keep the most specific row: a match on
    // the selected BU beats the tenant's company-wide override, which beats the
    // seeded system row — the same precedence ProgramRolesTab's picker applies.
    //
    // The row already stored on the contact always survives the collapse, even
    // when it is the seeded twin: dropping it would leave the trigger reading
    // "Select role…" over a role that is in fact set.
    const roleOptions = useMemo(()=>{
        const roleSpecificity = (v)=>v.businessUnitId && v.businessUnitId === businessUnitId ? 2 : v.isSystem ? 0 : 1;
        const byCode = new Map();
        for (const v of roleTypes.data ?? []){
            const kept = byCode.get(v.code || v.id);
            const wins = !kept || v.id === roleTypeId || kept.value !== roleTypeId && roleSpecificity(v) > roleSpecificity(kept);
            if (wins) {
                byCode.set(v.code || v.id, {
                    value: v.id,
                    label: v.displayLabel,
                    isSystem: v.isSystem,
                    businessUnitId: v.businessUnitId
                });
            }
        }
        return [
            ...byCode.values()
        ].map(({ value, label })=>({
                value,
                label
            }));
    }, [
        roleTypes.data,
        roleTypeId,
        businessUnitId
    ]);
    const roleLabel = (id)=>(allRoleTypes.data ?? []).find((v)=>v.id === id)?.displayLabel ?? '—';
    const companyHelpId = useId();
    const companyErrorId = useId();
    const platformAccessLabelId = useId();
    const platformAccessGroupName = useId();
    const hasAccountId = useId();
    const contactOnlyId = useId();
    const choosePlatformAccess = (choice)=>{
        setPlatformAccess(choice);
        // Switching to "contact only" drops the picked user rather than parking it
        // out of sight — a hidden id that resurfaces on the next edit is a link
        // nobody chose.
        if (choice === PLATFORM_CONTACT_ONLY) setPlatformUserId('');
    };
    // A stored link that matches no option needs its own item — without one
    // the trigger looks unset while the id still submits. Only a successful
    // load proves the profile is gone; on a failed load the link may be fine,
    // so the label must not claim deletion.
    const companyLinkUnknown = companyProfileId !== NO_SELECTION && !companiesQuery.isLoading && !companyOptions.some((opt)=>opt.value === companyProfileId);
    const columns = [
        {
            key: 'name',
            header: 'Name',
            render: (row)=>{
                const name = `${row.firstName} ${row.lastName}`.trim();
                const href = personHref(row.id);
                return /*#__PURE__*/ _jsxs("span", {
                    className: "flex items-center gap-2",
                    children: [
                        href ? // A real link, so Cmd/Ctrl-click and middle-click open a new tab;
                        // a plain click on the rest of the row navigates via onRowClick.
                        /*#__PURE__*/ _jsx(Link, {
                            to: href,
                            className: "hover:underline",
                            children: name
                        }) : name,
                        row.isActive === false && /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "neutral",
                            "data-testid": Testids.deliveryRegistries.contactInactiveBadge,
                            children: "Inactive"
                        })
                    ]
                });
            }
        },
        {
            key: 'contactType',
            header: 'Type',
            render: (row)=>contactTypeLabel(row.contactType)
        },
        {
            // The registry role drives program role-assignment locking, so it must
            // be visible from the list, not only inside the detail drawer.
            key: 'role',
            header: 'Role',
            render: (row)=>roleLabel(row.roleTypeId)
        },
        {
            key: 'title',
            header: 'Title'
        },
        {
            key: 'email',
            header: 'Email'
        },
        {
            key: 'affiliation',
            header: 'Affiliation',
            render: (row)=>row.governmentOrganizationName || row.companyProfileName || row.organization || '—'
        },
        {
            key: 'account',
            header: 'Account',
            // WS6: the stored link is what roster grants resolve through. Link
            // discovers by the contact's email (server refuses ambiguity); unlink
            // stops future roster grants without touching existing access.
            render: (row)=>row.platformUserId ? /*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "info",
                            children: "Linked"
                        }),
                        canEdit && /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            "data-testid": Testids.deliveryRegistries.contactUnlinkButton,
                            className: "text-xs text-muted-foreground hover:underline",
                            onClick: ()=>unlink.mutate({
                                    companyId: tenantId,
                                    contactId: row.id
                                }),
                            children: "Unlink"
                        })
                    ]
                }) : canEdit && row.email ? /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    "data-testid": Testids.deliveryRegistries.contactLinkButton,
                    "data-contact-id": row.id,
                    className: "text-xs font-medium text-primary hover:underline",
                    title: `Link this contact to the account with email ${row.email}`,
                    onClick: ()=>link.mutate({
                            companyId: tenantId,
                            contactId: row.id,
                            email: row.email
                        }),
                    children: "Link account"
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground/60",
                    children: "—"
                })
        }
    ];
    // Seed the out-of-form pickers (type / affiliation / role / platform) from a
    // record — null resets to create defaults. Shared by openCreate and the
    // drawer's onEditStart so both surfaces start from the same place.
    const seedFrom = (row)=>{
        setContactType(row?.contactType || 'INTERNAL');
        setGovernmentOrganizationId(row?.governmentOrganizationId ?? '');
        setCompanyProfileId(row?.companyProfileId || NO_SELECTION);
        setBusinessUnitId(row?.businessUnitId ?? '');
        setRoleTypeId(row?.roleTypeId ?? '');
        // A stored link is an answered "has an account". No link is NOT an answer:
        // legacy rows predate the question, so editing one asks it rather than
        // assuming the person was deliberately marked access-less.
        setPlatformAccess(row?.platformUserId ? PLATFORM_HAS_ACCOUNT : PLATFORM_UNANSWERED);
        setPlatformUserId(row?.platformUserId ?? '');
    };
    const openCreate = ()=>{
        seedFrom(null);
        setFormOpen(true);
    };
    // Shared by the create modal and the drawer edit-in-place; the target id is
    // passed in (empty = create). The identity fields ride in via `values` (RHF);
    // affiliation / role / platform come from parent state.
    const submitContact = async (values, id)=>{
        // The platform-access choice lives outside the declarative fields, so the
        // required check is manual — as with the role type on the Add Role form.
        if (contactType === 'INTERNAL') {
            if (platformAccess === PLATFORM_UNANSWERED) {
                throw new Error('Choose whether this person has a platform account.');
            }
            if (platformAccess === PLATFORM_HAS_ACCOUNT && !platformUserId) {
                throw new Error('Pick the platform user, or choose "Contact only — no platform access".');
            }
        }
        // The full-row source for pass-through fields (legacy organization, active
        // flag) — found by id since the drawer and modal track different records.
        const source = id ? items.find((c)=>c.id === id) ?? null : null;
        const chosenCompanyId = companyProfileId === NO_SELECTION ? '' : companyProfileId;
        const structuredAffiliation = contactType === 'GOVERNMENT' ? governmentOrganizationId : chosenCompanyId;
        await upsert.mutateAsync({
            id,
            companyId: tenantId,
            firstName: values.firstName ?? '',
            lastName: values.lastName ?? '',
            title: values.title ?? '',
            // This form owns both labeled addresses outright, so every submit sets
            // them explicitly — present-and-empty is a deliberate clear here, not
            // an omission. `email` itself is server-derived from these two, so it
            // is never sent at all.
            corporateEmail: values.corporateEmail ?? '',
            governmentEmail: values.governmentEmail ?? '',
            phone: values.phone ?? '',
            location: values.location ?? '',
            // The upsert is a full-row replace: stored legacy text survives edits
            // that pick no structured affiliation, but a real company or gov org
            // supersedes it — otherwise clearing the link resurrects dead text.
            organization: structuredAffiliation ? '' : source?.organization ?? '',
            contactType: contactType || 'INTERNAL',
            // One affiliation per type: whichever input the form hides is cleared,
            // not preserved — an invisible stale company link fails FK validation
            // on every later edit once that company is soft-deleted.
            companyProfileId: contactType === 'GOVERNMENT' ? '' : chosenCompanyId,
            governmentOrganizationId: contactType === 'GOVERNMENT' ? governmentOrganizationId : '',
            isActive: source?.isActive ?? true,
            // Business-unit scope applies to every contact type — unlike role/
            // platform it isn't an identity-type concern. Always sent explicitly:
            // '' is a deliberate clear (unscope), matching every other field here.
            businessUnitId,
            // The registry role only applies to internal team members; like the
            // affiliation, the hidden input clears rather than silently persisting.
            roleTypeId: contactType === 'INTERNAL' ? roleTypeId : '',
            // Only internal people can be platform users, so a non-internal type
            // clears the link like the hidden affiliation and role above — an
            // invisible link would otherwise keep pointing an access grant at a
            // government or vendor contact. '' is a deliberate clear server-side.
            platformUserId: contactType === 'INTERNAL' && platformAccess === PLATFORM_HAS_ACCOUNT ? platformUserId : ''
        });
    };
    const handleDeactivate = async ()=>{
        if (!deactivating) return;
        await retire.mutateAsync({
            companyId: tenantId,
            contactId: deactivating.id
        });
        setDeactivating(null);
    };
    const handleDelete = async ()=>{
        if (!deleting) return;
        await remove.mutateAsync(deleting.id);
        setDeleting(null);
    };
    // The type select — drives the affiliation / role / platform group. Shared by
    // the create modal (topContent) and the drawer's Type row (editControl).
    const contactTypePicker = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "contactType",
                children: "Contact type"
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: contactType,
                onValueChange: setContactType,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        id: "contactType",
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "Select type…"
                        })
                    }),
                    /*#__PURE__*/ _jsx(SelectContent, {
                        children: CONTACT_TYPE_OPTIONS.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                value: opt.value,
                                children: opt.label
                            }, opt.value))
                    })
                ]
            })
        ]
    });
    // Affiliation follows the type: GOVERNMENT gets the gov-org picker; every
    // other type gets the structured company select (with its load/error/deleted
    // edge handling).
    const affiliationPicker = contactType === 'GOVERNMENT' ? /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
        id: "contact-government-org",
        label: "Government organization",
        helpText: "For government contacts (CO, COR, agency POCs).",
        value: governmentOrganizationId,
        onChange: setGovernmentOrganizationId,
        testid: Testids.deliveryShared.govOrgSearchContact
    }) : /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "companyProfileId",
                children: contactType === 'EXTERNAL' ? 'Company' : 'Company affiliation'
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: companyProfileId,
                onValueChange: setCompanyProfileId,
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        id: "companyProfileId",
                        "aria-describedby": companiesQuery.isError ? `${companyErrorId} ${companyHelpId}` : companyHelpId,
                        disabled: companiesQuery.isLoading,
                        children: companiesQuery.isLoading ? 'Loading companies…' : /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "Select company…"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SelectContent, {
                        children: [
                            /*#__PURE__*/ _jsx(SelectItem, {
                                value: NO_SELECTION,
                                children: "None"
                            }),
                            companyLinkUnknown && /*#__PURE__*/ _jsx(SelectItem, {
                                value: companyProfileId,
                                children: companiesQuery.isError ? '(current selection unavailable)' : '(deleted company)'
                            }),
                            companyOptions.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                    value: opt.value,
                                    children: opt.label
                                }, opt.value))
                        ]
                    })
                ]
            }),
            companiesQuery.isError && /*#__PURE__*/ _jsx("p", {
                id: companyErrorId,
                className: "text-xs text-destructive",
                role: "alert",
                children: "Couldn't load companies — the list may be incomplete. Reopen the form to retry."
            }),
            /*#__PURE__*/ _jsx("p", {
                id: companyHelpId,
                className: "text-xs text-muted-foreground",
                children: contactType === 'EXTERNAL' ? 'The partner or vendor company this contact belongs to (register it under Companies if missing).' : 'Link internal contacts to a company profile.'
            })
        ]
    });
    // Business-unit scope (SAD-75) — every contact type carries this, unlike
    // role/platform below. Optional: None means unscoped, visible in every
    // BU's picker, which is the default and remains the common case. Picking
    // one immediately narrows the Role select below to that BU's names plus
    // the company-wide ones.
    const businessUnitPicker = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "businessUnitId",
                children: "Business unit"
            }),
            /*#__PURE__*/ _jsxs(Select, {
                value: businessUnitId || NO_SELECTION,
                onValueChange: (v)=>{
                    setBusinessUnitId(v === NO_SELECTION ? '' : v);
                    // Role options are scoped to the business unit — a role kept from
                    // the old BU would ride submitContact into a mismatched upsert.
                    setRoleTypeId('');
                },
                children: [
                    /*#__PURE__*/ _jsx(SelectTrigger, {
                        id: "businessUnitId",
                        "data-testid": Testids.deliveryRegistries.contactBusinessUnitSelect,
                        children: /*#__PURE__*/ _jsx(SelectValue, {
                            placeholder: "Select business unit…"
                        })
                    }),
                    /*#__PURE__*/ _jsxs(SelectContent, {
                        children: [
                            /*#__PURE__*/ _jsx(SelectItem, {
                                value: NO_SELECTION,
                                children: "None — unscoped, visible everywhere"
                            }),
                            businessUnitOptions.map((opt)=>/*#__PURE__*/ _jsx(SelectItem, {
                                    value: opt.value,
                                    children: opt.label
                                }, opt.value))
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Optional. Scopes this contact to one business unit and narrows the Role list below to it; leave as None to keep this contact visible everywhere."
            })
        ]
    });
    // The registry role — only internal team members carry one.
    const rolePicker = contactType === 'INTERNAL' ? /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        children: [
            /*#__PURE__*/ _jsx(Label, {
                htmlFor: "roleTypeId",
                children: "Role"
            }),
            /*#__PURE__*/ _jsx(SpecraCombobox, {
                id: "roleTypeId",
                label: "Role",
                options: [
                    {
                        value: NO_SELECTION,
                        label: 'None'
                    },
                    ...roleOptions
                ],
                value: roleTypeId || NO_SELECTION,
                onValueChange: (v)=>setRoleTypeId(!v || v === NO_SELECTION ? '' : v),
                placeholder: "Select role…",
                searchPlaceholder: "Search roles…"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Their standing role. Program role assignments inherit it and treat it as fixed — leave as None to let each program pick."
            })
        ]
    }) : null;
    // Platform access — the has-account / contact-only choice plus the user
    // picker. Internal only, like the role above.
    const platformPicker = contactType === 'INTERNAL' ? /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1.5",
        "data-testid": Testids.deliveryRegistries.contactPlatformAccess,
        children: [
            /*#__PURE__*/ _jsx(Label, {
                id: platformAccessLabelId,
                children: "Platform access"
            }),
            /*#__PURE__*/ _jsxs("div", {
                role: "radiogroup",
                "aria-labelledby": platformAccessLabelId,
                className: "space-y-1.5",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                type: "radio",
                                name: platformAccessGroupName,
                                value: PLATFORM_HAS_ACCOUNT,
                                checked: platformAccess === PLATFORM_HAS_ACCOUNT,
                                onChange: ()=>choosePlatformAccess(PLATFORM_HAS_ACCOUNT),
                                id: hasAccountId,
                                "data-testid": Testids.deliveryRegistries.contactHasPlatformAccount
                            }),
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: hasAccountId,
                                className: "font-normal",
                                children: "Has a platform account"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                type: "radio",
                                name: platformAccessGroupName,
                                value: PLATFORM_CONTACT_ONLY,
                                checked: platformAccess === PLATFORM_CONTACT_ONLY,
                                onChange: ()=>choosePlatformAccess(PLATFORM_CONTACT_ONLY),
                                id: contactOnlyId,
                                "data-testid": Testids.deliveryRegistries.contactOnlyNoPlatform
                            }),
                            /*#__PURE__*/ _jsx(Label, {
                                htmlFor: contactOnlyId,
                                className: "font-normal",
                                children: "Contact only — no platform access"
                            })
                        ]
                    })
                ]
            }),
            platformAccess === PLATFORM_HAS_ACCOUNT && /*#__PURE__*/ _jsx("div", {
                className: "pt-1",
                children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                    id: "platformUserId",
                    value: platformUserId,
                    onChange: setPlatformUserId,
                    testid: Testids.deliveryRegistries.contactPlatformUserSearch
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-xs text-muted-foreground",
                children: "Recording someone with no account is fine — their details stand on their own. Linking an account is what a later access grant is built from."
            })
        ]
    }) : null;
    // Stacked affiliation group — the create modal renders it below the
    // declarative identity fields (extraContent).
    const affiliationContent = /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            affiliationPicker,
            businessUnitPicker,
            rolePicker,
            platformPicker
        ]
    });
    // Drawer inline-edit config, bound to the viewed record. The editable controls
    // live on the rows below; this carries only the form-level concerns and seeds
    // the out-of-form pickers on entering edit.
    const detailEdit = detail ? {
        defaultValues: {
            id: detail.id,
            firstName: detail.firstName ?? '',
            lastName: detail.lastName ?? '',
            title: detail.title ?? '',
            corporateEmail: detail.corporateEmail ?? '',
            governmentEmail: detail.governmentEmail ?? '',
            phone: detail.phone ?? '',
            location: detail.location ?? ''
        },
        onSubmit: (v)=>submitContact(v, detail.id),
        isSubmitting: upsert.isPending,
        submitLabel: 'Save changes',
        onEditStart: ()=>seedFrom(detail)
    } : undefined;
    // Detail sections: read values plus, per row, the inline-edit control. Identity
    // rows carry an `editField` (bound to the drawer's form); type / affiliation /
    // role / platform carry an `editControl` (the parent-controlled pickers, which
    // are self-conditional on the current type — role/platform render nothing for
    // non-internal contacts, so those rows fall back to their read value in edit).
    const affiliationDisplay = (r)=>r.governmentOrganizationName || r.companyProfileName || r.organization || '—';
    const detailSections = detail ? [
        {
            heading: 'Identity',
            rows: [
                {
                    label: 'First name',
                    value: detail.firstName || '—',
                    editField: fieldByName('firstName')
                },
                {
                    label: 'Last name',
                    value: detail.lastName || '—',
                    editField: fieldByName('lastName')
                },
                {
                    label: 'Corporate title',
                    value: detail.title || '—',
                    editField: fieldByName('title')
                }
            ]
        },
        {
            heading: 'Contact',
            rows: [
                {
                    label: 'Company or corporate email',
                    value: detail.corporateEmail || '—',
                    editField: fieldByName('corporateEmail')
                },
                {
                    label: 'Government or agency email',
                    value: detail.governmentEmail || '—',
                    editField: fieldByName('governmentEmail')
                },
                {
                    label: 'Phone',
                    value: detail.phone || '—',
                    editField: fieldByName('phone')
                },
                {
                    label: 'Location',
                    value: detail.location || '—',
                    editField: fieldByName('location')
                }
            ]
        },
        {
            heading: 'Classification',
            rows: [
                {
                    label: 'Type',
                    value: contactTypeLabel(detail.contactType),
                    editControl: contactTypePicker
                },
                {
                    label: 'Current affiliation',
                    value: affiliationDisplay(detail),
                    editControl: affiliationPicker
                },
                {
                    label: 'Business unit',
                    value: businessUnitLabel(detail.businessUnitId),
                    editControl: businessUnitPicker
                },
                {
                    label: 'Standing internal role',
                    value: roleLabel(detail.roleTypeId),
                    editControl: rolePicker
                },
                {
                    label: 'Account',
                    value: detail.platformUserId ? 'Linked' : '—',
                    editControl: platformPicker
                }
            ]
        }
    ] : [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(SectionNote, {
                children: "The company-wide directory of people. Contact type decides where a person can be used: Internal contacts can be given a program Role; Government and External contacts can be added to a program's Contacts."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "flex justify-end",
                children: canEdit && /*#__PURE__*/ _jsx(SpecraButton, {
                    onClick: openCreate,
                    "data-testid": Testids.deliveryRegistries.contactCreate,
                    children: "New Contact"
                })
            }),
            recordUnavailable && /*#__PURE__*/ _jsx(RecordUnavailable, {
                testId: Testids.deliveryRegistries.recordUnavailable,
                onDismiss: closeRecord
            }),
            /*#__PURE__*/ _jsx(SpecraInput, {
                "data-testid": Testids.deliveryRegistries.contactSearchInput,
                placeholder: "Search by name or email",
                value: search,
                onChange: (e)=>setSearch(e.target.value),
                className: "max-w-sm"
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "ghost",
                "aria-pressed": showInactive,
                onClick: ()=>setShowInactive((v)=>!v),
                "data-testid": Testids.deliveryRegistries.contactShowInactive,
                children: showInactive ? 'Hide inactive contacts' : 'Show inactive contacts'
            }),
            fullPage && peopleMovementOn && /*#__PURE__*/ _jsx(SpecraButton, {
                asChild: true,
                variant: "secondary",
                children: /*#__PURE__*/ _jsx(Link, {
                    to: "/registries/people-movement",
                    "data-testid": Testids.deliveryRegistries.peopleMovementLink,
                    children: "People movement →"
                })
            }),
            !fullPage && !isLoading && items.length > REGISTRY_ROW_CAP && /*#__PURE__*/ _jsxs("p", {
                className: "text-[length:var(--fs-ui-caption)] text-muted-foreground",
                children: [
                    "Showing the first ",
                    REGISTRY_ROW_CAP.toLocaleString(),
                    " of ",
                    items.length.toLocaleString(),
                    ' ',
                    items.length === 1 ? 'contact' : 'contacts',
                    " — search by name or email to narrow the list."
                ]
            }),
            /*#__PURE__*/ _jsx(EntityTable, {
                data: visibleItems,
                columns: fullPage ? [
                    {
                        key: 'name',
                        header: 'Person',
                        render: (row)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: /*#__PURE__*/ _jsx(Link, {
                                    to: personHref(row.id) ?? '#',
                                    children: `${row.firstName} ${row.lastName}`.trim()
                                }),
                                description: [
                                    contactTypeLabel(row.contactType),
                                    row.email
                                ].filter(Boolean).join(' · '),
                                trailing: row.isActive === false ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "neutral",
                                    "data-testid": Testids.deliveryRegistries.contactInactiveBadge,
                                    children: "Inactive"
                                }) : undefined
                            })
                    },
                    columns[2],
                    {
                        key: 'now',
                        header: 'Now',
                        render: (row)=>{
                            const s = summaryById.get(row.id);
                            const current = s?.current[0];
                            return current ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: [
                                    current.title,
                                    current.employer?.name || current.employerText
                                ].filter(Boolean).join(', ') || '—',
                                description: current.startDate ? `since ${current.startDate.toDate().toLocaleDateString('en-US', {
                                    month: 'short',
                                    year: 'numeric',
                                    timeZone: 'UTC'
                                })}` : current.fromContactForm ? 'Entered on the contact form' : current.source?.text,
                                trailing: s && s.current.length > 1 ? `+${s.current.length - 1}` : undefined
                            }) : '—';
                        }
                    },
                    {
                        key: 'before',
                        header: 'Before',
                        render: (row)=>summaryById.get(row.id)?.priorEmployers.map((ref)=>ref.name).filter(Boolean).join(' · ') || '—'
                    },
                    {
                        key: 'appears',
                        header: 'Shows up on',
                        render: (row)=>summaryById.get(row.id)?.topAppearances.map((ref)=>ref.name).filter(Boolean).join(' · ') || '—'
                    },
                    {
                        key: 'touch',
                        header: 'Last touch',
                        render: (row)=>{
                            const touch = summaryById.get(row.id)?.lastTouch;
                            return touch?.at ? /*#__PURE__*/ _jsx(SpecraEntityCell, {
                                title: touch.at.toDate().toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    timeZone: 'UTC'
                                }),
                                description: touch.context?.name
                            }) : '—';
                        }
                    },
                    columns[6]
                ] : columns,
                testid: Testids.deliveryRegistries.contactsTable,
                rowTestid: Testids.deliveryRegistries.contactRow,
                rowKey: (c)=>c.id,
                isLoading: isLoading,
                onRowClick: (row)=>{
                    rowOpen.onRowClick(row.id);
                },
                hideRowActions: !canEdit && !fullPage,
                rowActions: (row)=>/*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2 justify-end",
                        children: [
                            rowOpen.quickLookAction(row.id),
                            canEdit && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        // Edit-in-place: open the drawer straight into edit mode; its
                                        // onEditStart seeds the pickers from this record.
                                        onClick: ()=>{
                                            setDetailMode('edit');
                                            openRecord(row.id);
                                        },
                                        "data-testid": Testids.deliveryRegistries.contactEdit,
                                        children: "Edit"
                                    }),
                                    row.isActive === false ? /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        disabled: reactivate.isPending,
                                        onClick: ()=>reactivate.mutate({
                                                companyId: tenantId,
                                                contactId: row.id
                                            }),
                                        "data-testid": Testids.deliveryRegistries.contactReactivate,
                                        children: "Reactivate"
                                    }) : /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        onClick: ()=>setDeactivating(row),
                                        "data-testid": Testids.deliveryRegistries.contactDeactivate,
                                        children: "Deactivate"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "ghost",
                                        className: "text-danger hover:bg-danger-muted hover:text-danger",
                                        onClick: ()=>setDeleting(row),
                                        "data-testid": Testids.deliveryRegistries.contactDelete,
                                        children: "Delete"
                                    })
                                ]
                            })
                        ]
                    })
            }),
            fullPage && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    paging.hasMore && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        "data-testid": Testids.deliveryRegistries.showMore,
                        onClick: paging.showMore,
                        children: "Show more"
                    }),
                    /*#__PURE__*/ _jsx(RegistryListNote, {
                        children: "Most people here never get a login: customers, competitors and teammates are rows, not users. Jobs are kept as history."
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(EntityFormDialog, {
                open: formOpen,
                onOpenChange: setFormOpen,
                title: "Create Contact",
                fields: CONTACT_FIELDS,
                defaultValues: {
                    id: '',
                    firstName: '',
                    lastName: '',
                    title: '',
                    corporateEmail: '',
                    governmentEmail: '',
                    phone: '',
                    location: ''
                },
                topContent: contactTypePicker,
                extraContent: affiliationContent,
                onSubmit: async (values)=>{
                    await submitContact(values, '');
                    setFormOpen(false);
                },
                isSubmitting: upsert.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deactivating,
                onOpenChange: (open)=>!open && setDeactivating(null),
                title: "Deactivate Contact",
                description: deactivating ? `Deactivate "${deactivating.firstName} ${deactivating.lastName}"? They will no longer be offered for new selections. Their employment history, touchpoints and staffing stay as they are, and you can reactivate them from the inactive list.` : undefined,
                confirmLabel: "Deactivate",
                pendingLabel: "Deactivating…",
                onConfirm: handleDeactivate,
                isPending: retire.isPending
            }),
            /*#__PURE__*/ _jsx(ConfirmDeleteDialog, {
                open: !!deleting,
                onOpenChange: (open)=>!open && setDeleting(null),
                title: "Delete Contact",
                description: deleting ? `Remove "${deleting.firstName} ${deleting.lastName}" from the contacts directory? They will be hidden from every list. To keep them reachable and reversible, use Deactivate instead.` : undefined,
                onConfirm: handleDelete,
                isPending: remove.isPending
            }),
            /*#__PURE__*/ _jsxs(ScoreDetailDrawer, {
                open: !!detail,
                onOpenChange: (open)=>!open && closeRecord(),
                title: detail ? `${detail.firstName} ${detail.lastName}`.trim() || 'Contact' : 'Contact',
                sections: detailSections,
                testid: Testids.deliveryRegistries.contactDetailDrawer,
                edit: detailEdit,
                canEdit: canEdit,
                initialMode: detailMode,
                children: [
                    detail && /*#__PURE__*/ _jsx(OpenFullPage, {
                        kind: "CONTACT",
                        id: detail.id
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.deliveryRegistries.contactEmploymentSection,
                        className: "space-y-3",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Employment history"
                            }),
                            employments.map((e)=>{
                                const started = e.startDate ? formatContractDate(e.startDate) : '?';
                                const ended = e.endDate ? formatContractDate(e.endDate) : 'present';
                                return /*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border border-border/60 p-2 text-[12.5px]",
                                    "data-testid": Testids.deliveryRegistries.contactEmploymentRow,
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium",
                                                    children: e.employerName || '(unknown employer)'
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "flex items-center gap-1",
                                                    children: [
                                                        e.source === 'DERIVED' && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                            variant: "neutral",
                                                            children: "Derived"
                                                        }),
                                                        e.source === 'IMPORT' && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                            variant: "info",
                                                            children: "Imported"
                                                        }),
                                                        !e.endDate && /*#__PURE__*/ _jsx(SpecraBadge, {
                                                            variant: "success",
                                                            children: "Current"
                                                        })
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: [
                                                e.title,
                                                e.employmentType !== 'UNKNOWN' ? e.employmentType : ''
                                            ].filter(Boolean).join(' · ') || '—'
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between text-muted-foreground",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        started,
                                                        " – ",
                                                        ended
                                                    ]
                                                }),
                                                canCurate && !e.endDate && /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    "data-testid": Testids.deliveryRegistries.contactEmploymentEnd,
                                                    onClick: ()=>endEmployment.mutate({
                                                            companyId: tenantId,
                                                            id: e.id,
                                                            endDate: Timestamp.fromDate(new Date())
                                                        }),
                                                    children: "End today"
                                                })
                                            ]
                                        })
                                    ]
                                }, e.id);
                            }),
                            openEmployments > 1 && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-warning",
                                children: "Two or more employments are open-ended — fine for a consultant, but if one has actually ended, stamp its end date so the primary employer derives correctly."
                            }),
                            employments.length === 0 && !employmentsQuery.isLoading && /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "No employment history yet — changing this contact's company in the form records it automatically, or add a row here with dates when you know them."
                            }),
                            canCurate && detail && /*#__PURE__*/ _jsxs("div", {
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: empKind,
                                                onValueChange: (v)=>setEmpKind(v),
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "h-9 w-36 text-xs",
                                                        "aria-label": "Employer kind",
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                                        children: [
                                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                                value: "COMPANY",
                                                                children: "Company"
                                                            }),
                                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                                value: "GOV_ORG",
                                                                children: "Government org"
                                                            })
                                                        ]
                                                    })
                                                ]
                                            }),
                                            empKind === 'COMPANY' ? /*#__PURE__*/ _jsxs(Select, {
                                                value: empCompanyId,
                                                onValueChange: setEmpCompanyId,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "h-9 flex-1 min-w-44 text-xs",
                                                        "aria-label": "Employer",
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                                            placeholder: "Pick a company…"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: (companiesQuery.data?.items ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: c.id,
                                                                children: c.name
                                                            }, c.id))
                                                    })
                                                ]
                                            }) : /*#__PURE__*/ _jsx("div", {
                                                className: "flex-1 min-w-44",
                                                children: /*#__PURE__*/ _jsx(GovernmentOrgPicker, {
                                                    id: "contact-employment-gov-org",
                                                    testid: Testids.deliveryShared.govOrgSearchContact,
                                                    label: "",
                                                    value: empGovOrgId,
                                                    onChange: setEmpGovOrgId
                                                })
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                className: "h-9 flex-1 min-w-32 text-xs",
                                                placeholder: "Title (optional)",
                                                value: empTitle,
                                                onChange: (e)=>setEmpTitle(e.target.value)
                                            }),
                                            /*#__PURE__*/ _jsxs(Select, {
                                                value: empType,
                                                onValueChange: setEmpType,
                                                children: [
                                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                                        className: "h-9 w-36 text-xs",
                                                        "aria-label": "Employment type",
                                                        children: /*#__PURE__*/ _jsx(SelectValue, {})
                                                    }),
                                                    /*#__PURE__*/ _jsx(SelectContent, {
                                                        children: [
                                                            'EMPLOYEE',
                                                            'CONTRACTOR',
                                                            'ADVISOR',
                                                            'GOV_CIVILIAN',
                                                            'MILITARY',
                                                            'UNKNOWN'
                                                        ].map((t)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                                value: t,
                                                                children: t.split('_').join(' ').toLowerCase()
                                                            }, t))
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-wrap items-center gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                className: "h-9 w-36 text-xs",
                                                "aria-label": "Start date (optional)",
                                                value: empStart,
                                                onChange: (e)=>setEmpStart(e.target.value)
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraInput, {
                                                type: "date",
                                                className: "h-9 w-36 text-xs",
                                                "aria-label": "End date (optional)",
                                                value: empEnd,
                                                onChange: (e)=>setEmpEnd(e.target.value)
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                disabled: (empKind === 'COMPANY' ? !empCompanyId : !empGovOrgId) || createEmployment.isPending,
                                                "data-testid": Testids.deliveryRegistries.contactEmploymentAdd,
                                                onClick: ()=>{
                                                    if (!detail) return;
                                                    createEmployment.mutate({
                                                        companyId: tenantId,
                                                        contactId: detail.id,
                                                        companyProfileId: empKind === 'COMPANY' ? empCompanyId : undefined,
                                                        governmentOrganizationId: empKind === 'GOV_ORG' ? empGovOrgId : undefined,
                                                        title: empTitle,
                                                        employmentType: empType,
                                                        startDate: ymdToTimestamp(empStart),
                                                        endDate: ymdToTimestamp(empEnd)
                                                    }, {
                                                        onSuccess: ()=>{
                                                            setEmpCompanyId('');
                                                            setEmpGovOrgId('');
                                                            setEmpTitle('');
                                                            setEmpStart('');
                                                            setEmpEnd('');
                                                        }
                                                    });
                                                },
                                                children: "Add"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    detail && /*#__PURE__*/ _jsx(ContactDetail, {
                        contactId: detail.id
                    }),
                    canCurate && detail && /*#__PURE__*/ _jsxs("div", {
                        className: "mt-[22px] space-y-2 border-t border-border/60 pt-[18px]",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                children: "Merge duplicate"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-xs text-muted-foreground",
                                children: "If this row and another are the same person, merge this one into the survivor. Touchpoints, staffing lines, and history follow; the merge is audited and reversible."
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-wrap items-center gap-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraCombobox, {
                                        className: "h-9 min-w-52 flex-1 text-xs",
                                        label: "Surviving contact",
                                        options: items.filter((c)=>c.id !== detail.id).map((c)=>{
                                            // Employer chip: the scary case is two REAL people sharing a
                                            // name at the same prime — affiliation is the disambiguator
                                            // that keeps a merge honest.
                                            const aff = c.governmentOrganizationName || c.companyProfileName;
                                            return {
                                                value: c.id,
                                                label: `${`${c.firstName} ${c.lastName}`.trim()}${c.email ? ` — ${c.email}` : ''}${aff ? ` · ${aff}` : ''}`
                                            };
                                        }),
                                        value: mergeTargetId || undefined,
                                        onValueChange: (v)=>setMergeTargetId(v ?? ''),
                                        placeholder: "Pick the surviving contact…",
                                        searchPlaceholder: "Search contacts by name, email or employer…",
                                        triggerProps: {
                                            'aria-label': 'Surviving contact',
                                            'data-testid': Testids.deliveryRegistries.contactMergeSelect
                                        }
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                        size: "sm",
                                        variant: "destructive",
                                        disabled: !mergeTargetId || mergeContacts.isPending,
                                        "data-testid": Testids.deliveryRegistries.contactMergeButton,
                                        onClick: ()=>mergeContacts.mutate({
                                                companyId: tenantId,
                                                loserContactId: detail.id,
                                                survivorContactId: mergeTargetId
                                            }, {
                                                // Follow the drawer to the surviving identity.
                                                onSuccess: (resp)=>openRecord(resp.survivor?.id ?? mergeTargetId)
                                            }),
                                        children: "Merge into selected"
                                    })
                                ]
                            }),
                            mergeEvents.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                "data-testid": Testids.deliveryRegistries.contactMergeHistorySection,
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-[10.5px] font-medium uppercase tracking-[0.08em] text-muted-foreground",
                                        children: "Merge history"
                                    }),
                                    mergeEvents.map((ev)=>{
                                        const mine = detail && ev.survivorContactId === detail.id;
                                        return /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center justify-between gap-2 rounded-md border border-border/60 p-2 text-[12.5px]",
                                            children: [
                                                /*#__PURE__*/ _jsxs("span", {
                                                    children: [
                                                        mine ? 'Absorbed' : 'Merged into another contact:',
                                                        ' ',
                                                        /*#__PURE__*/ _jsx("span", {
                                                            className: "font-medium",
                                                            children: ev.loserName || 'unnamed contact'
                                                        }),
                                                        /*#__PURE__*/ _jsxs("span", {
                                                            className: "text-muted-foreground",
                                                            children: [
                                                                ' · ',
                                                                ev.createdAt ? ev.createdAt.toDate().toLocaleDateString() : ''
                                                            ]
                                                        })
                                                    ]
                                                }),
                                                ev.reversedAt ? /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    variant: "neutral",
                                                    children: "Reversed"
                                                }) : canCurate && mine && /*#__PURE__*/ _jsx(SpecraButton, {
                                                    size: "sm",
                                                    variant: "ghost",
                                                    disabled: unmergeContacts.isPending,
                                                    "data-testid": Testids.deliveryRegistries.contactMergeUndo,
                                                    onClick: ()=>unmergeContacts.mutate({
                                                            companyId: tenantId,
                                                            contactId: ev.loserContactId
                                                        }),
                                                    children: "Undo"
                                                })
                                            ]
                                        }, ev.id);
                                    })
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
